import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { Button, PageContainer, Spinner } from '@shared';

import { FilePicker } from '@/components/FilePicker';
import { MDENTURE_PRODUCTION_CENTER_ID } from '@/config/mdenture';
import { useAuth } from '@/features/auth/useAuth';
import { useTranslations } from '@/i18n/client';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import { BrandingApi } from '@/services/api/branding-api';
import { OrganizationApi } from '@/services/api/organization-api';
import { OrdersApi } from '@/services/api/orders-api';
import type { AddressDto, DeliveryMethodsResult, OrderArticlePrice } from '@/services/api/types';
import { useUploadProgress } from '@/features/upload/useUploadProgress';
import type { UploadProgressOperation } from '@/features/upload/upload-progress-context';

import {
  isSelectedPackageSelection,
  loadSelectedPackageSelection,
  PACKAGES,
  saveSelectedPackageSelection,
  type PackageDef,
  type SelectedPackageSelection,
} from './new-case-selection';
import { parseOrderFileFilters, selectDesignFiles } from './design-folder-filter';
import { isAbortError } from './orders-page-utils';

function asLookupValue(value: string | undefined): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function trimmedOrEmpty(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

function parseCutoffMinutes(value: string | null | undefined): number | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(value ?? '');
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

function calculateExpectedDeliveryDate(
  maxHandlingTime: number,
  deliveryDays: number,
  deliveryCutoffTime: string | null | undefined,
  now = new Date(),
): Date {
  const dueDate = new Date(now);
  let extraDay = 0;

  if (dueDate.getUTCDay() === 0) {
    dueDate.setUTCDate(dueDate.getUTCDate() + 1);
  } else if (dueDate.getUTCDay() === 6) {
    dueDate.setUTCDate(dueDate.getUTCDate() + 2);
  } else {
    const cutoffMinutes = parseCutoffMinutes(deliveryCutoffTime);
    const currentMinutes = dueDate.getUTCHours() * 60 + dueDate.getUTCMinutes();
    if (cutoffMinutes != null && currentMinutes >= cutoffMinutes) {
      extraDay = 1;
    }
  }

  const handlingTime = Math.max(0, maxHandlingTime) + Math.max(0, deliveryDays) + extraDay;
  const totalDays = handlingTime + 2 * Math.floor((handlingTime + dueDate.getUTCDay() - 1) / 5);
  dueDate.setUTCDate(dueDate.getUTCDate() + totalDays);
  return dueDate;
}

const DELIVERY_DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const trimmed = trimmedOrEmpty(value);
    if (trimmed.length > 0) {
      return trimmed;
    }
  }

  return '';
}

function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

function formatOrganizationAddressOption(address: AddressDto): string {
  const location = [trimmedOrEmpty(address.zip), trimmedOrEmpty(address.city)]
    .filter((part) => part.length > 0)
    .join(' ');
  const main = firstNonEmpty(address.name, location, address.street, address.id);
  const details = [trimmedOrEmpty(address.street), location, trimmedOrEmpty(address.country)]
    .filter((part) => part.length > 0)
    .join(', ');

  return details.length > 0 ? `${main} - ${details}` : main;
}

interface OrderDataFormState {
  reference: string;
  notes: string;
  internalCaseId: string;
}

const SCAN_FIELDS = ['upperjaw', 'lowerjaw', 'aestheticplate', 'allinone'] as const;
type ScanField = (typeof SCAN_FIELDS)[number];
type ScanFiles = Partial<Record<ScanField, File>>;

const REQUIRED_INDIVIDUAL_SCAN_FIELDS: readonly ScanField[] = ['upperjaw', 'lowerjaw', 'aestheticplate'];
const CUSTOM_SHADE_OPTIONS = [
  'A4',
  'B1',
  'B2',
  'B3',
  'B4',
  'C1',
  'C2',
  'C3',
  'C4',
  'D2',
  'D3',
  'D4',
] as const;
const ON_DEMAND_CUSTOM_SHADES = new Set(['B1', 'B2', 'B3', 'B4', 'C1', 'C2', 'C4', 'D2', 'D3', 'D4']);

function prepareScanFileForUpload(field: ScanField, file: File): File {
  const lowerName = file.name.toLowerCase();
  if (SCAN_FIELDS.some((marker) => lowerName.includes(marker))) {
    return file;
  }

  const extensionIndex = file.name.lastIndexOf('.');
  const baseName = extensionIndex > 0 ? file.name.slice(0, extensionIndex) : file.name;
  const extension = extensionIndex > 0 ? file.name.slice(extensionIndex) : '';

  return new File([file], `${baseName}-${field}${extension}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

function hasRequiredScanSet(scanFiles: ScanFiles): boolean {
  const hasAllIndividualScans = REQUIRED_INDIVIDUAL_SCAN_FIELDS.every((field) => Boolean(scanFiles[field]));
  return hasAllIndividualScans || Boolean(scanFiles.allinone);
}

const ORDER_REFERENCE_PATTERN = /^[0-9]*$/;
const TERMS_URL = 'https://www.merz-dental.de/agb';
const PRIVACY_POLICY_URL = 'https://www.merz-dental.de/datenschutz';

type MdDesignFinishingKey =
  'ahLine' | 'torusCover' | 'anteriorAnatomy' | 'noAnteriorAnatomy' | 'palatalRugae';

function detectFinishingKey(article: OrderArticlePrice): MdDesignFinishingKey | null {
  const source = `${article.productID} ${article.description}`.toLowerCase();

  if (
    source.includes('no_anterior_anatomy') ||
    source.includes('no anterior anatomy') ||
    source.includes('keine anatom')
  ) {
    return 'noAnteriorAnatomy';
  }

  if (
    source.includes('anterior_anatomy') ||
    source.includes('anterior anatomy') ||
    source.includes('anatomische gestaltung')
  ) {
    return 'anteriorAnatomy';
  }

  if (
    source.includes('ah_line') ||
    source.includes('ah line') ||
    source.includes('ah-linie') ||
    source.includes('ah linie')
  ) {
    return 'ahLine';
  }

  if (source.includes('torus_cover') || source.includes('torus cover') || source.includes('torus')) {
    return 'torusCover';
  }

  if (
    source.includes('palatal_rugae') ||
    source.includes('palatal rugae') ||
    source.includes('gaumenfalten')
  ) {
    return 'palatalRugae';
  }

  return null;
}

function findPackage(packageId: SelectedPackageSelection['packageId']): PackageDef | null {
  return PACKAGES.find((pkg) => pkg.id === packageId) ?? null;
}

function selectionFromLocation(state: unknown): SelectedPackageSelection | null {
  return isSelectedPackageSelection(state) ? state : null;
}

export function NewCaseDetailsPage() {
  const t = useTranslations('newCase.caseStart');
  const tCommon = useTranslations('newCase');
  const tPackage = useTranslations('newCase.packages');
  const tOrderDetails = useTranslations('common.orderDetails');
  const tStatus = useTranslations('common.status');
  const tErrors = useTranslations('errors.api');
  const { client, user } = useAuth();
  const { beginUpload, progress: activeUpload } = useUploadProgress();
  const companyId = asLookupValue(user?.orgID ?? undefined);
  const location = useLocation();
  const navigate = useNavigate();

  const initialSelection = useMemo(() => {
    const stateSelection = selectionFromLocation(location.state);
    if (stateSelection) {
      return stateSelection;
    }

    return loadSelectedPackageSelection();
  }, [location.state]);

  const [selection, setSelection] = useState<SelectedPackageSelection | null>(initialSelection);
  const [orderData, setOrderData] = useState<OrderDataFormState>({
    reference: '',
    notes: '',
    internalCaseId: '',
  });
  const [orderReferenceError, setOrderReferenceError] = useState(false);
  const [toothShade, setToothShade] = useState('vita_a2');
  const [customShade, setCustomShade] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'standard' | 'express' | 'pickup'>('standard');
  const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethodsResult | null>(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [organizationAddresses, setOrganizationAddresses] = useState<readonly AddressDto[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [addressErrorKey, setAddressErrorKey] = useState<ApiErrorKey | null>(null);
  const [designArticles, setDesignArticles] = useState<readonly OrderArticlePrice[]>([]);
  const [selectedDesignArticleIds, setSelectedDesignArticleIds] = useState<readonly string[]>([]);
  const [hasLoadedDesignArticles, setHasLoadedDesignArticles] = useState(false);
  const [isLoadingDesignArticles, setIsLoadingDesignArticles] = useState(false);
  const [designArticleErrorKey, setDesignArticleErrorKey] = useState<ApiErrorKey | null>(null);
  const [designFileFilterRules, setDesignFileFilterRules] = useState<readonly RegExp[] | null>(null);
  const [isLoadingDesignFileFilter, setIsLoadingDesignFileFilter] = useState(false);
  const [designFileFilterErrorKey, setDesignFileFilterErrorKey] = useState<ApiErrorKey | null>(null);
  const [designFileFilterParseError, setDesignFileFilterParseError] = useState(false);
  const [designFileSelectionError, setDesignFileSelectionError] = useState(false);
  const [designFiles, setDesignFiles] = useState<readonly File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrorKey, setSubmitErrorKey] = useState<ApiErrorKey | null>(null);
  const [protocolFile, setProtocolFile] = useState<File | null>(null);
  const [protocolFileError, setProtocolFileError] = useState(false);
  const [scanFiles, setScanFiles] = useState<ScanFiles>({});
  const [scanFileError, setScanFileError] = useState<{
    field: string;
    fileName: string;
    existingField: string;
  } | null>(null);
  const isMillOrder = selection?.packageId === 'mill';

  useEffect(() => {
    if (selection) {
      saveSelectedPackageSelection(selection);
    }
  }, [selection]);

  useEffect(() => {
    if (!client || !companyId) {
      return;
    }

    const abort = new AbortController();
    const api = new OrganizationApi(client);

    queueMicrotask(() => {
      setIsLoadingAddresses(true);
      setAddressErrorKey(null);
    });

    void api
      .getAddresses(abort.signal)
      .then((data) => {
        const availableAddresses = isMillOrder ? data.filter((address) => address.isShippingAddress) : data;
        setOrganizationAddresses(availableAddresses);

        const preferredAddress = isMillOrder
          ? availableAddresses.length === 1
            ? availableAddresses[0]
            : null
          : (availableAddresses.find((address) => address.isBillingAddress) ?? availableAddresses[0] ?? null);
        if (preferredAddress) {
          setSelectedAddressId(preferredAddress.id);
        } else {
          setSelectedAddressId('');
        }
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }

        setOrganizationAddresses([]);
        setSelectedAddressId('');
        setAddressErrorKey(errorKeyOf(error));
      })
      .finally(() => {
        setIsLoadingAddresses(false);
      });

    return () => {
      abort.abort();
    };
  }, [client, companyId, isMillOrder]);

  useEffect(() => {
    if (!client || !companyId || !isMillOrder) {
      return;
    }

    const abort = new AbortController();
    const api = new BrandingApi(client);

    queueMicrotask(() => {
      setIsLoadingDesignFileFilter(true);
      setDesignFileFilterErrorKey(null);
      setDesignFileFilterParseError(false);
      setDesignFileFilterRules(null);
      setDesignFiles([]);
      setDesignFileSelectionError(false);
    });

    void api
      .getFileFilter(abort.signal)
      .then((xml) => {
        try {
          setDesignFileFilterRules(parseOrderFileFilters(xml));
        } catch {
          setDesignFileFilterRules(null);
          setDesignFileFilterParseError(true);
        }
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }

        setDesignFileFilterRules(null);
        setDesignFileFilterErrorKey(errorKeyOf(error));
      })
      .finally(() => {
        setIsLoadingDesignFileFilter(false);
      });

    return () => {
      abort.abort();
    };
  }, [client, companyId, isMillOrder]);

  useEffect(() => {
    if (!client || !companyId || isMillOrder) {
      return;
    }

    const abort = new AbortController();
    const api = new OrdersApi(client);

    queueMicrotask(() => {
      setIsLoadingDesignArticles(true);
      setDesignArticleErrorKey(null);
    });

    void api
      .getArticles(
        {
          articleCategories: ['MDDesign'],
          productionCenterId: MDENTURE_PRODUCTION_CENTER_ID,
          companyId,
        },
        abort.signal,
      )
      .then((data) => {
        const mdDesignArticles = data.filter((article) => article.category === 'MDDesign');
        setDesignArticles(mdDesignArticles);
        setHasLoadedDesignArticles(true);

        setSelectedDesignArticleIds((current) =>
          current.filter((value) => mdDesignArticles.some((article) => article.productID === value)),
        );
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }

        setDesignArticles([]);
        setSelectedDesignArticleIds([]);
        setHasLoadedDesignArticles(false);
        setDesignArticleErrorKey(errorKeyOf(error));
      })
      .finally(() => {
        setIsLoadingDesignArticles(false);
      });

    return () => {
      abort.abort();
    };
  }, [client, companyId, isMillOrder]);

  useEffect(() => {
    if (!client || !companyId) {
      return;
    }

    const abort = new AbortController();
    const api = new OrdersApi(client);

    void api
      .getDeliveryMethods(MDENTURE_PRODUCTION_CENTER_ID, companyId, abort.signal)
      .then((data) => {
        setDeliveryMethods(data);
        if (data.standardPrice != null) {
          setDeliveryMethod('standard');
        } else if (data.expressPrice != null) {
          setDeliveryMethod('express');
        } else if (data.hasPickup) {
          setDeliveryMethod('pickup');
        }
      })
      .catch((error) => {
        if (!isAbortError(error)) {
          setDeliveryMethods(null);
        }
      });

    return () => {
      abort.abort();
    };
  }, [client, companyId]);

  const orderReference = orderData.reference.trim();
  const hasOrderReference = orderReference.length > 0 && ORDER_REFERENCE_PATTERN.test(orderReference);
  const hasRequiredUpload = isMillOrder
    ? designFiles.some((file) => !file.name.toLowerCase().endsWith('.constructioninfo'))
    : hasRequiredScanSet(scanFiles);
  const hasToothColor = isMillOrder || toothShade !== 'custom' || customShade.trim().length > 0;
  const missingRequirements: Array<{ key: string; text: string }> = [];

  if (!selectedAddressId) {
    missingRequirements.push({
      key: 'shipping-address',
      text: t('sections.final.missingRequirements.shippingAddress'),
    });
  }

  if (!hasOrderReference) {
    missingRequirements.push({
      key: 'order-number',
      text: t('sections.final.missingRequirements.orderNumber'),
    });
  }

  if (isMillOrder) {
    if (!hasRequiredUpload) {
      missingRequirements.push({
        key: 'design-files',
        text: t('sections.final.missingRequirements.designFiles'),
      });
    }
  } else {
    const hasValidIndividualScanSet = REQUIRED_INDIVIDUAL_SCAN_FIELDS.every((field) =>
      Boolean(scanFiles[field]),
    );
    const hasValidAllInOneScan = Boolean(scanFiles.allinone);

    if (!hasValidIndividualScanSet && !hasValidAllInOneScan) {
      const missingFields = REQUIRED_INDIVIDUAL_SCAN_FIELDS.filter((field) => {
        return !scanFiles[field];
      })
        .map((field) => t(`sections.upload.fields.${field}.label`))
        .join(', ');

      missingRequirements.push({
        key: 'scan-set',
        text: t('sections.final.missingRequirements.individualScans', { fields: missingFields }),
      });
    }
  }

  if (!hasToothColor) {
    missingRequirements.push({
      key: 'tooth-shade',
      text: t('sections.final.missingRequirements.toothShade'),
    });
  }

  if (!acceptTerms) {
    missingRequirements.push({
      key: 'terms',
      text: t('sections.final.missingRequirements.terms'),
    });
  }

  const isSubmitDisabled = missingRequirements.length > 0;

  const handleSubmit = async () => {
    if (
      !client ||
      !companyId ||
      !selectedAddressId ||
      !selection ||
      !hasOrderReference ||
      !hasRequiredUpload ||
      !hasToothColor ||
      activeUpload
    ) {
      return;
    }

    setIsSubmitting(true);
    setSubmitErrorKey(null);

    const api = new OrdersApi(client);
    let uploadProgress: UploadProgressOperation | null = null;
    try {
      const articleProductIds = Array.from(
        new Set(
          [selection.selectedVariantValue, ...selectedDesignArticleIds]
            .filter((value) => !isMillOrder || value === selection.selectedVariantValue)
            .map((value) => value.trim())
            .filter((value) => value.length > 0),
        ),
      );

      const newOrderId = await api.createDirectOrder({
        productionCenterId: MDENTURE_PRODUCTION_CENTER_ID,
        companyId,
        reference: orderReference,
        notes: orderData.notes || null,
        toothColor: isMillOrder ? null : toothShade === 'custom' ? customShade.trim() || null : toothShade,
        articleProductIds,
        ...(isMillOrder ? { designFileNames: designFiles.map((file) => file.name) } : {}),
        addressId: selectedAddressId,
        deliveryMethod: deliveryMethod === 'express' ? 1 : deliveryMethod === 'pickup' ? 2 : 0,
        internalCaseId: orderData.internalCaseId.trim() || null,
      });

      const scanFilesToUpload = isMillOrder
        ? designFiles.map((file) => ({ file, category: 'ScannerData' as const }))
        : SCAN_FIELDS.flatMap((field) => {
            const file = scanFiles[field];
            return file
              ? [{ file: prepareScanFileForUpload(field, file), category: 'ScannerData' as const }]
              : [];
          });
      const filesToUpload = [
        ...(protocolFile ? [{ file: protocolFile, category: 'OrderData' as const }] : []),
        ...scanFilesToUpload,
      ];
      uploadProgress = beginUpload(filesToUpload.length);
      if (!uploadProgress) {
        return;
      }

      for (const { file, category } of filesToUpload) {
        uploadProgress.startFile(file.name);
        await api.uploadScanFile(newOrderId, file, category, undefined, (percentage) =>
          uploadProgress?.setFileProgress(percentage),
        );
        uploadProgress.completeFile();
      }

      uploadProgress.finish();
      uploadProgress = null;
      navigate(`/cases/${newOrderId}`);
    } catch (error) {
      if (!isAbortError(error)) {
        setSubmitErrorKey(errorKeyOf(error));
      }
    } finally {
      uploadProgress?.finish();
      setIsSubmitting(false);
    }
  };

  if (!selection) {
    return (
      <PageContainer className="md-page--wide md-new-case-details-page max-sm:w-full max-sm:px-0 max-sm:[&_.md-case-card]:p-4">
        <section className="md-case-card">
          <h1>{t('missingSelection.title')}</h1>
          <p>{t('missingSelection.body')}</p>
          <p>
            <Link to="/cases/new">{t('missingSelection.action')}</Link>
          </p>
        </section>
      </PageContainer>
    );
  }

  const pkg = findPackage(selection.packageId);

  if (!pkg) {
    return (
      <PageContainer className="md-page--wide md-new-case-details-page max-sm:w-full max-sm:px-0 max-sm:[&_.md-case-card]:p-4">
        <section className="md-case-card">
          <h1>{t('missingSelection.title')}</h1>
          <p>{t('missingSelection.body')}</p>
          <p>
            <Link to="/cases/new">{t('missingSelection.action')}</Link>
          </p>
        </section>
      </PageContainer>
    );
  }

  const variants = selection.variants;
  const selectedVariant =
    variants.find((variant) => variant.value === selection.selectedVariantValue) ?? variants[0] ?? null;
  const designHandlingTime = designArticles
    .filter((article) => selectedDesignArticleIds.includes(article.productID))
    .map((article) => article.handlingTime)
    .reduce((maximum, handlingTime) => Math.max(maximum, handlingTime), 0);
  const selectedDeliveryDays =
    deliveryMethod === 'express'
      ? deliveryMethods?.expressDays
      : deliveryMethod === 'pickup'
        ? 0
        : deliveryMethods?.standardDays;
  const expectedDeliveryDate =
    selectedDeliveryDays != null
      ? calculateExpectedDeliveryDate(
          (selectedVariant?.handlingTime ?? 3) + designHandlingTime,
          selectedDeliveryDays,
          deliveryMethods?.deliveryCutoffTime,
        )
      : null;
  const shadeOptions =
    pkg.id === 'pro'
      ? ['vita_a1', 'vita_a2', 'vita_a3', 'vita_a35', 'custom']
      : ['vita_a1', 'vita_a2', 'vita_a3', 'vita_a35'];

  return (
    <PageContainer className="md-page--wide md-new-case-details-page max-sm:w-full max-sm:px-0 max-sm:[&_.md-case-card]:p-4 max-sm:[&_.md-case-hero]:p-4 max-sm:[&_.md-case-summary_h1]:text-[1.7rem] max-sm:[&_.md-case-summary_h1]:leading-[1.1] max-sm:[&_.md-case-summary__box]:p-[0.85rem] max-sm:[&_.md-case-choice-card]:gap-2 max-sm:[&_.md-case-choice-card]:p-[0.65rem] max-sm:[&_.md-case-check-row_span]:min-w-0 max-sm:[&_.md-case-check-row_span]:wrap-anywhere max-sm:[&_.md-case-actions]:flex-col max-sm:[&_.md-case-actions]:items-stretch max-sm:[&_.md-case-actions_.zs-button]:w-full max-sm:[&_.md-case-actions_a]:self-start">
      <section className="md-case-hero max-sm:mb-4">
        <span className="md-case-eyebrow">{t('hero.eyebrow')}</span>
        <h1>{isMillOrder ? t('hero.millTitle') : t('hero.title')}</h1>
        <p>{isMillOrder ? t('hero.millBody') : t('hero.body')}</p>
      </section>

      <section className="md-case-card mb-4">
        <div className="md-case-summary">
          <div>
            <h1>{tPackage(`${pkg.id}.name`)}</h1>
            <p className="md-case-summary__subtitle">{tPackage(`${pkg.id}.subtitle`)}</p>
            <p className="md-case-summary__desc">{tPackage(`${pkg.id}.desc`)}</p>

            <ul className="md-package-card__features md-case-summary__features">
              {Array.from({ length: pkg.featureCount }, (_, i) => (
                <li key={i}>
                  <span>
                    <strong>{tPackage(`${pkg.id}.feature${i}title`)}</strong>
                    <small>{tPackage(`${pkg.id}.feature${i}desc`)}</small>
                  </span>
                </li>
              ))}
            </ul>

            <div className="md-case-summary__actions">
              <Link to="/cases/new">{t('summary.changePackage')}</Link>
            </div>
          </div>

          <aside className="md-case-summary__box">
            <span>{t('summary.variantLabel')}</span>
            <select
              className="zs-field__input"
              aria-label={t('summary.variantLabel')}
              value={selection.selectedVariantValue}
              onChange={(event) => {
                const nextValue = event.target.value;
                setSelection((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    selectedVariantValue: nextValue,
                  };
                });
              }}
            >
              {variants.map((variant) => (
                <option key={variant.value} value={variant.value}>
                  {variant.label}
                </option>
              ))}
            </select>

            <div className="md-case-summary__price-preview">
              <strong>{selectedVariant?.priceLabel ?? '-'}</strong>
              <small>{t('summary.priceNote')}</small>
            </div>
          </aside>
        </div>
      </section>

      <form className="md-case-form" onSubmit={(event) => event.preventDefault()} noValidate>
        <section className="md-case-card">
          <h3>{t('sections.orderData.title')}</h3>
          <p>{t('sections.orderData.body')}</p>
          {isLoadingAddresses ? (
            <p className="text-xs text-[var(--zs-muted)] mb-2">{tStatus('loading')}</p>
          ) : null}
          {addressErrorKey ? (
            <p className="text-sm text-[var(--md-danger)] mb-2">{tErrors(addressErrorKey)}</p>
          ) : null}
          <div className="md-case-grid md-case-grid--two">
            <label className="md-field md-case-grid__full">
              <span className="md-field__label">{t('sections.orderData.shippingAddress')}</span>
              <select
                className="md-field__input"
                value={selectedAddressId}
                onChange={(event) => {
                  setSelectedAddressId(event.target.value);
                }}
                disabled={organizationAddresses.length === 0}
              >
                <option value="">
                  {organizationAddresses.length === 0
                    ? t('sections.orderData.noAddressOptions')
                    : t('sections.orderData.shippingAddressPlaceholder')}
                </option>
                {organizationAddresses.map((address) => (
                  <option key={address.id} value={address.id}>
                    {formatOrganizationAddressOption(address)}
                  </option>
                ))}
              </select>
            </label>
            <label className="md-field md-case-grid__full">
              <span className="md-field__label">{t('sections.orderData.orderNumber')}</span>
              <input
                className="md-field__input"
                type="text"
                inputMode="numeric"
                placeholder={t('sections.orderData.orderNumberPlaceholder')}
                value={orderData.reference}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (ORDER_REFERENCE_PATTERN.test(raw)) {
                    setOrderReferenceError(false);
                    setOrderData((prev) => ({ ...prev, reference: raw }));
                  } else {
                    setOrderReferenceError(true);
                  }
                }}
                required
              />
              {orderReferenceError ? (
                <small className="text-[var(--md-danger)]">{t('sections.orderData.orderNumberError')}</small>
              ) : null}
            </label>
            <label className="md-field md-case-grid__full">
              <span className="md-field__label">{tOrderDetails('orderDataSection.internalCaseId')}</span>
              <input
                className="md-field__input"
                type="text"
                placeholder={t('sections.orderData.internalCaseIdPlaceholder')}
                value={orderData.internalCaseId}
                onChange={(event) =>
                  setOrderData((prev) => ({ ...prev, internalCaseId: event.target.value }))
                }
              />
            </label>
          </div>
        </section>

        <section className="md-case-card">
          <h3>{t('sections.protocol.title')}</h3>
          <p>{t('sections.protocol.body')}</p>
          <FilePicker
            id="protocol-file"
            label={t('sections.protocol.label')}
            accept="application/pdf,.pdf"
            selectedFiles={protocolFile ? [protocolFile] : []}
            hint={
              protocolFileError ? (
                <span className="text-[var(--md-danger)]">{t('sections.protocol.invalidFile')}</span>
              ) : (
                t('sections.protocol.hint')
              )
            }
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              if (!file) {
                setProtocolFile(null);
                setProtocolFileError(false);
                return;
              }

              if (!isPdfFile(file)) {
                setProtocolFile(null);
                setProtocolFileError(true);
                event.currentTarget.value = '';
                return;
              }

              setProtocolFile(file);
              setProtocolFileError(false);
            }}
          />
        </section>

        {!isMillOrder ? (
          <section className="md-case-card">
            <h3>{t('sections.shade.title')}</h3>
            <p>{t('sections.shade.body')}</p>

            <div className="md-case-choice-grid">
              {shadeOptions.map((option) => (
                <label key={option} className="md-case-choice-card">
                  <input
                    type="radio"
                    name="toothShade"
                    value={option}
                    checked={toothShade === option}
                    onChange={(event) => setToothShade(event.target.value)}
                  />
                  <span>
                    <strong>{t(`sections.shade.options.${option}.title`)}</strong>
                    <small>{t(`sections.shade.options.${option}.desc`)}</small>
                  </span>
                </label>
              ))}
            </div>

            {toothShade === 'custom' ? (
              <label className="md-field">
                <span className="md-field__label">{t('sections.shade.customLabel')}</span>
                <select
                  className="md-field__input"
                  value={customShade}
                  onChange={(event) => setCustomShade(event.target.value)}
                >
                  <option value="">{t('sections.shade.customPlaceholder')}</option>
                  {CUSTOM_SHADE_OPTIONS.map((shade) => (
                    <option key={shade} value={shade}>
                      {ON_DEMAND_CUSTOM_SHADES.has(shade)
                        ? `${shade} ${t('sections.shade.onDemandSuffix')}`
                        : shade}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {isLoadingDesignArticles ? (
              <div className="flex flex-col items-center justify-center gap-2 py-4" aria-live="polite">
                <Spinner />
                <p className="text-xs text-[var(--zs-muted)] m-0">{tStatus('loading')}</p>
              </div>
            ) : (
              <>
                {designArticleErrorKey ? (
                  <p className="text-sm text-[var(--md-danger)]">{tErrors(designArticleErrorKey)}</p>
                ) : null}

                <div className="md-case-choice-grid">
                  {designArticles.map((article) => {
                    const optionId = article.productID;
                    const isSelected = selectedDesignArticleIds.includes(optionId);
                    const finishingKey = detectFinishingKey(article);
                    const title = article.description || article.productID;
                    const subtitle = finishingKey
                      ? t(`sections.shade.finishings.${finishingKey}.desc`)
                      : article.description
                        ? article.productID
                        : '';

                    return (
                      <label key={optionId} className="md-case-choice-card">
                        <input
                          type="checkbox"
                          name={`finish-${optionId}`}
                          value={optionId}
                          checked={isSelected}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setSelectedDesignArticleIds((current) => {
                              if (checked) {
                                if (current.includes(optionId)) {
                                  return current;
                                }

                                return [...current, optionId];
                              }

                              return current.filter((value) => value !== optionId);
                            });
                          }}
                        />
                        <span>
                          <strong>{title}</strong>
                          <small>{subtitle}</small>
                        </span>
                      </label>
                    );
                  })}
                </div>

                {hasLoadedDesignArticles && designArticles.length === 0 && !designArticleErrorKey ? (
                  <p className="text-sm text-[var(--zs-muted)]">{tCommon('noArticleVariants')}</p>
                ) : null}
              </>
            )}
          </section>
        ) : null}

        {isMillOrder ? (
          <section className="md-case-card">
            <h3>{t('sections.designFolder.title')}</h3>
            <p>{t('sections.designFolder.body')}</p>
            <FilePicker
              id="design-folder"
              label={t('sections.designFolder.label')}
              buttonText={t('sections.designFolder.select')}
              hint={
                isLoadingDesignFileFilter ? (
                  tStatus('loading')
                ) : designFileFilterErrorKey ? (
                  <span className="text-[var(--md-danger)]">{tErrors(designFileFilterErrorKey)}</span>
                ) : designFileFilterParseError ? (
                  <span className="text-[var(--md-danger)]">{t('sections.designFolder.invalidFilter')}</span>
                ) : designFileSelectionError ? (
                  <span className="text-[var(--md-danger)]">
                    {t('sections.designFolder.noMatchingFiles')}
                  </span>
                ) : (
                  t('sections.designFolder.hint')
                )
              }
              selectedFiles={designFiles}
              selectedFilesLabel={t('sections.designFolder.filesSelected', {
                count: designFiles.length,
              })}
              multiple
              allowDirectory
              disabled={isLoadingDesignFileFilter || designFileFilterRules == null}
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                const filteredFiles = designFileFilterRules
                  ? selectDesignFiles(files, designFileFilterRules)
                  : [];
                setDesignFiles(filteredFiles);
                setDesignFileSelectionError(files.length > 0 && filteredFiles.length === 0);
              }}
            />
          </section>
        ) : (
          <section className="md-case-card">
            <h3>{t('sections.upload.title')}</h3>
            <p>{t('sections.upload.body')}</p>
            <div className="md-case-grid md-case-grid--two">
              {SCAN_FIELDS.map((field) => {
                const selectedFile = scanFiles[field];
                const fieldLabel = t(`sections.upload.fields.${field}.label`);

                return (
                  <div key={field} className="flex flex-col gap-2">
                    <FilePicker
                      label={fieldLabel}
                      accept=".stl,.STL,.ply,.PLY,.obj,.OBJ"
                      selectedFiles={selectedFile ? [selectedFile] : []}
                      hint={t(`sections.upload.fields.${field}.hint`)}
                      trailingAction={
                        selectedFile ? (
                          <Button
                            type="button"
                            variant="danger"
                            className="shrink-0 self-center"
                            aria-label={t('sections.upload.removeFile', { field: fieldLabel })}
                            title={t('sections.upload.removeFile', { field: fieldLabel })}
                            onClick={() => {
                              setScanFiles((prev) => ({ ...prev, [field]: undefined }));
                            }}
                          >
                            {t('sections.upload.removeFileLabel')}
                          </Button>
                        ) : null
                      }
                      onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;

                        if (file) {
                          const normalizedFileName = file.name.toLowerCase();
                          const duplicate = Object.entries(scanFiles).find(
                            ([existingField, existingFile]) =>
                              existingField !== field &&
                              existingFile?.name.toLowerCase() === normalizedFileName,
                          );

                          if (duplicate?.[1]) {
                            setScanFileError({
                              field,
                              fileName: file.name,
                              existingField: duplicate[0],
                            });
                            event.currentTarget.value = '';
                            return;
                          }
                        }

                        setScanFiles((prev) => ({ ...prev, [field]: file ?? undefined }));
                        setScanFileError((current) => (current?.field === field ? null : current));
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="md-case-card">
          <h3>{isMillOrder ? t('sections.delivery.millTitle') : t('sections.delivery.title')}</h3>
          <p>{t('sections.delivery.body')}</p>

          <div className="md-case-delivery">
            <div className="md-case-choice-grid md-case-choice-grid--single">
              {deliveryMethods?.standardPrice != null && (
                <label className="md-case-choice-card">
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="standard"
                    checked={deliveryMethod === 'standard'}
                    onChange={() => setDeliveryMethod('standard')}
                  />
                  <span>
                    <strong>{t('sections.delivery.options.standard.title')}</strong>
                    <small>
                      {deliveryMethods.standardPrice.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {deliveryMethods.currency}
                    </small>
                  </span>
                </label>
              )}
              {deliveryMethods?.expressPrice != null && (
                <label className="md-case-choice-card">
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="express"
                    checked={deliveryMethod === 'express'}
                    onChange={() => setDeliveryMethod('express')}
                  />
                  <span>
                    <strong>{t('sections.delivery.options.express.title')}</strong>
                    <small>
                      {deliveryMethods.expressPrice.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {deliveryMethods.currency}
                    </small>
                  </span>
                </label>
              )}
              {deliveryMethods?.hasPickup && (
                <label className="md-case-choice-card">
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="pickup"
                    checked={deliveryMethod === 'pickup'}
                    onChange={() => setDeliveryMethod('pickup')}
                  />
                  <span>
                    <strong>{t('sections.delivery.options.pickup.title')}</strong>
                    <small>{t('sections.delivery.options.pickup.desc')}</small>
                  </span>
                </label>
              )}
              {deliveryMethods == null && (
                <>
                  {(['standard', 'express'] as const).map((option) => (
                    <label key={option} className="md-case-choice-card">
                      <input
                        type="radio"
                        name="shippingMethod"
                        value={option}
                        checked={deliveryMethod === option}
                        onChange={() => setDeliveryMethod(option)}
                      />
                      <span>
                        <strong>{t(`sections.delivery.options.${option}.title`)}</strong>
                        <small>{t(`sections.delivery.options.${option}.desc`)}</small>
                      </span>
                    </label>
                  ))}
                </>
              )}
            </div>

            <div className="md-case-delivery__date-box">
              <span>{t('delivery.label')}</span>
              <strong>
                {expectedDeliveryDate ? DELIVERY_DATE_FORMATTER.format(expectedDeliveryDate) : '-'}
              </strong>
              <small>{t('delivery.note')}</small>
            </div>
          </div>
        </section>

        <section className="md-case-card">
          <h3>{isMillOrder ? t('sections.final.millTitle') : t('sections.final.title')}</h3>
          <label className="md-field">
            <span className="md-field__label">{t('sections.final.notes')}</span>
            <textarea
              className="md-field__input"
              rows={6}
              placeholder={t('sections.final.notesPlaceholder')}
              value={orderData.notes}
              onChange={(event) => setOrderData((prev) => ({ ...prev, notes: event.target.value }))}
            />
          </label>

          <label className="md-case-check-row flex items-start gap-[0.55rem] text-[0.9rem] text-[var(--zs-muted)]">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(event) => setAcceptTerms(event.target.checked)}
            />
            <span className="min-w-0 max-sm:wrap-anywhere">
              {t.rich('sections.final.terms', {
                terms: (chunks) => (
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:no-underline"
                  >
                    {chunks}
                  </a>
                ),
                privacy: (chunks) => (
                  <a
                    href={PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:no-underline"
                  >
                    {chunks}
                  </a>
                ),
              })}
            </span>
          </label>

          <div className="mt-4 flex flex-wrap items-center gap-[0.9rem] max-sm:flex-col max-sm:items-stretch">
            <Button
              type="button"
              className="max-sm:w-full"
              loading={isSubmitting}
              loadingLabel={t('sections.final.submitting')}
              disabled={isSubmitDisabled}
              onClick={() => void handleSubmit()}
            >
              {t('sections.final.submit')}
            </Button>
            <Link className="max-sm:self-start" to="/cases/new">
              {t('sections.final.backToPackage')}
            </Link>
          </div>
          {isSubmitDisabled ? (
            <div className="mt-3 text-sm text-[var(--zs-muted)]" role="status" aria-live="polite">
              <p className="mb-1 font-semibold">{t('sections.final.missingRequirements.title')}</p>
              <ul className="m-0 list-disc space-y-1 pl-5">
                {missingRequirements.map(({ key, text }) => (
                  <li key={key}>{text}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {submitErrorKey ? (
            <p className="text-sm text-[var(--md-danger)] mt-2">{tErrors(submitErrorKey)}</p>
          ) : null}
        </section>
      </form>
      {scanFileError ? (
        <div className="md-review-dialog" role="presentation">
          <section
            className="md-review-dialog__panel"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="duplicate-scan-title"
            aria-describedby="duplicate-scan-description"
          >
            <h2 id="duplicate-scan-title">{t('sections.upload.duplicateFileTitle')}</h2>
            <p id="duplicate-scan-description" className="md-review-dialog__description">
              {t('sections.upload.duplicateFile', {
                fileName: scanFileError.fileName,
                field: t(`sections.upload.fields.${scanFileError.existingField}.label`),
              })}
            </p>
            <div className="md-review-dialog__actions">
              <Button autoFocus onClick={() => setScanFileError(null)}>
                {t('sections.upload.duplicateFileClose')}
              </Button>
            </div>
          </section>
        </div>
      ) : null}
    </PageContainer>
  );
}
